import React from 'react';
import { motion } from 'framer-motion';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';

import ContactForm from '../components/contact/ContactForm';
import Icon from '../components/common/Icon';
import PublicPricing from '../components/pricing/PublicPricing';
import DesktopDownload from '../components/download/DesktopDownload';
import DemoVideoSection from '../components/landing/DemoVideoSection';
import HeroVideoPreview from '../components/landing/HeroVideoPreview';
import BouncingLanguages from '../components/landing/BouncingLanguages';
import { useAuth } from '../hooks/useAuth';
import { getAuthenticatedHomePath } from '../utils/authRedirects';

const SUPPORTED_LANGUAGES = {
  gujarati: { name: 'Gujarati', native: 'ગુજરાતી', flag: '🇮🇳', text: 'આ મીટિંગ ક્યારે શરૂ થશે?' },
  english: { name: 'English', native: 'English', flag: '🇬🇧', text: 'When will this meeting start?' },
  hindi: { name: 'Hindi', native: 'हिंदी', flag: '🇮🇳', text: 'यह मीटिंग कब शुरू होगी?' },
  tamil: { name: 'Tamil', native: 'தமிழ்', flag: '🇮🇳', text: 'இந்த கூட்டம் எப்போது தொடங்கும்?' },
  japanese: { name: 'Japanese', native: '日本語', flag: '🇯🇵', text: 'この会議はいつ始まりますか？' },
  german: { name: 'German', native: 'Deutsch', flag: '🇩🇪', text: 'Wann beginnt dieses Treffen?' },
  spanish: { name: 'Spanish', native: 'Español', flag: '🇪🇸', text: '¿Cuándo comenzará esta reunión?' },
  french: { name: 'French', native: 'Français', flag: '🇫🇷', text: 'Quand cette réunion va-t-elle commencer ?' }
};

// Input that means the visitor has taken over scrolling from a #section link.
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'];

// The six conversation scenarios. They were six hand-written copies of the
// same markup, which is why every one of them had to be edited to change the
// shape of a single event; as data they share one template and one entrance
// sequence. `transcript` is what is heard, `ai` is what KrishvaQ makes of it.
const CS_VALUE_CLASS = {
  accent: 'cs-tag-cyan',
  high: 'cs-priority-high',
  plain: 'cs-value-strong'
};

const USE_CASES = [
  {
    id: 'interviews',
    title: 'Technical Interview',
    icon: 'brain',
    badge: 'TECHNICAL INTERVIEW',
    topic: 'Concurrency / Mutex',
    confidence: 96,
    transcript: [
      { time: '10:31:02 AM', kind: 'speech', label: 'INTERVIEWER', text: '"What is a Mutex?"' },
      { time: '10:31:03 AM', kind: 'system', label: 'SPEECH DETECTED', detail: 'Language detected:', value: 'English', wave: true },
      { time: '10:31:04 AM', kind: 'system', label: 'INTENT DETECTED', tags: [{ text: 'Technical Question', tone: 'tag-topic' }, { text: 'Context understood', tone: 'tag-quiet' }] }
    ],
    ai: {
      brand: 'KRISHVAQ AI RESPONSE',
      time: '10:31:05 AM',
      text: 'A Mutex is a synchronization mechanism that controls access to a shared resource.',
      ready: { label: 'ANSWER READY', readAloud: true }
    }
  },
  {
    id: 'business',
    title: 'Business Meeting',
    icon: 'briefcase',
    badge: 'BUSINESS MEETING',
    topic: 'Product Roadmap',
    confidence: 96,
    transcript: [
      { time: '10:31:02 AM', kind: 'speech', label: 'PROJECT LEAD', text: '"We should move the launch date to the second week of September."' },
      { time: '10:31:03 AM', kind: 'system', label: 'KEY DECISION DETECTED', detail: 'Launch target:', value: 'Second week of September', tags: [{ text: 'Decision', tone: 'tag-decision' }, { text: 'Important', tone: 'tag-important' }] },
      { time: '10:31:04 AM', kind: 'system', label: 'ACTION ITEM', detail: 'Confirm final launch date with engineering team.', tags: [{ text: 'Action Item', tone: 'tag-action' }] }
    ],
    ai: {
      brand: 'AI SUMMARY',
      time: '10:31:05 AM',
      text: 'Important meeting decision & action item captured automatically.'
    }
  },
  {
    id: 'client',
    title: 'Client Call',
    icon: 'phone',
    badge: 'CLIENT CALL',
    topic: 'User Requirements',
    confidence: 96,
    transcript: [
      { time: '10:31:02 AM', kind: 'speech', label: 'CLIENT', text: '"We need the dashboard to support three different user roles."' },
      { time: '10:31:03 AM', kind: 'system', label: 'REQUIREMENT DETECTED', detail: 'User roles requested:', value: 'Admin, Manager, Member', valueTone: 'accent' },
      { time: '10:31:04 AM', kind: 'system', label: 'PRIORITY ASSIGNED', detail: 'Priority:', value: 'HIGH', valueTone: 'high' }
    ],
    ai: {
      brand: 'AI UNDERSTANDING',
      time: '10:31:05 AM',
      text: 'Client requirement logged & added to call summary.'
    }
  },
  {
    id: 'multilingual',
    title: 'Multilingual Conversation',
    icon: 'globe',
    badge: 'MULTILINGUAL CONVERSATION',
    topic: 'Meeting Schedule',
    confidence: 96,
    transcript: [
      { time: '10:31:02 AM', kind: 'speech', label: 'CLIENT (GUJARATI)', text: '"\u0A86 \u0AAE\u0AC0\u0A9F\u0ABF\u0A82\u0A97 \u0A95\u0ACD\u0AAF\u0ABE\u0AB0\u0AC7 \u0AB6\u0AB0\u0AC2 \u0AA5\u0AB6\u0AC7?"' },
      { time: '10:31:03 AM', kind: 'system', label: 'LANGUAGE DETECTED', detail: 'Source language:', value: 'Gujarati', valueTone: 'accent' },
      { time: '10:31:04 AM', kind: 'system', label: 'LIVE TRANSLATION', icon: '\ud83c\udf10', translation: true, detail: 'Translated to English:', value: '"When will this meeting start?"' }
    ],
    ai: {
      brand: 'CONTEXT PRESERVED',
      time: '10:31:05 AM',
      text: 'Translation complete & context preserved in real-time.'
    }
  },
  {
    id: 'learning',
    title: 'Learning & Training',
    icon: 'book',
    badge: 'LEARNING & TRAINING',
    topic: 'Thread Safety',
    confidence: 96,
    transcript: [
      { time: '10:31:02 AM', kind: 'speech', label: 'INSTRUCTOR', text: '"A Mutex prevents multiple threads from accessing a shared resource simultaneously."' },
      { time: '10:31:03 AM', kind: 'system', label: 'CONCEPT DETECTED', detail: 'Topic:', value: 'Concurrency / Thread Safety', valueTone: 'accent' }
    ],
    ai: {
      brand: 'KRISHVAQ SIMPLIFIED EXPLANATION',
      time: '10:31:04 AM',
      text: '"Think of a Mutex like a key to a room. Only the person holding the key can enter."',
      ready: { label: 'EXPLANATION READY', readAloud: true }
    }
  },
  {
    id: 'everyday',
    title: 'Everyday Conversation',
    icon: 'message',
    badge: 'EVERYDAY CONVERSATION',
    topic: 'Appointment Change',
    confidence: 96,
    transcript: [
      { time: '10:31:02 AM', kind: 'speech', label: 'USER', text: '"Can you help me understand what they just said?"' },
      { time: '10:31:03 AM', kind: 'system', label: 'CONTEXT UNDERSTOOD', detail: 'Topic:', value: 'Appointment Change' }
    ],
    ai: {
      brand: 'KRISHVAQ CLARIFICATION',
      time: '10:31:04 AM',
      text: "The speaker is discussing moving tomorrow's appointment from 2:00 PM to 4:00 PM.",
      ready: { label: 'UNDERSTANDING READY' }
    }
  }
];

// The two columns of the language network. They are pools, not fixed sets:
// the FROM / TO selects below offer every language in either direction, so a
// column swaps a member out rather than letting the live pair fall off the
// diagram (picking German as the source used to leave the input side dark).
const SOURCE_NODES = ['gujarati', 'hindi', 'tamil', 'japanese'];
const TARGET_NODES = ['english', 'german', 'spanish', 'french'];

// Trace geometry, in the order the column rows are drawn. x 220 / 780 are the
// inner edges of the two 22% columns and y 55 / 165 / 275 / 385 the centres of
// the four equal rows, so each endpoint meets the card it belongs to.
const TRACE_PATHS_IN = [
  'M 220 55 C 330 55, 400 220, 500 220',
  'M 220 165 C 330 165, 420 220, 500 220',
  'M 220 275 C 330 275, 420 220, 500 220',
  'M 220 385 C 330 385, 400 220, 500 220'
];
const TRACE_PATHS_OUT = [
  'M 500 220 C 600 220, 670 55, 780 55',
  'M 500 220 C 580 220, 670 165, 780 165',
  'M 500 220 C 580 220, 670 275, 780 275',
  'M 500 220 C 600 220, 670 385, 780 385'
];

// A language that belongs to this column's own pool leaves the order alone, so
// clicking a node never reshuffles the cards; one picked from the selects leads
// the column instead. `blocked` is the other column - no language is on both
// sides at once - and any hole that leaves is filled from the remaining set.
function pickColumn(pinned, pool, blocked) {
  const out = pool.includes(pinned) ? [] : [pinned];
  for (const lang of pool) {
    if (out.length >= 4) break;
    if (lang !== pinned && blocked.includes(lang)) continue;
    if (!out.includes(lang)) out.push(lang);
  }
  for (const lang of Object.keys(SUPPORTED_LANGUAGES)) {
    if (out.length >= 4) break;
    if (!out.includes(lang) && !blocked.includes(lang)) out.push(lang);
  }
  return out;
}

// One trace: the connection itself, plus a packet that travels along it in the
// path's own direction (source -> engine, engine -> target) while it is live.
// `pathLength` normalises the dash to the path, so the packet is unaffected by
// the horizontal stretch the viewBox takes on.
function NetworkTrace({ d, live, direction }) {
  return (
    <>
      <path
        d={d}
        className={`trace-path ${live ? 'trace-live' : ''}`}
        fill="none"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
      {live && (
        <path
          d={d}
          pathLength="100"
          className={`trace-packet ${direction === 'out' ? 'trace-packet-out' : ''}`}
          fill="none"
          vectorEffect="non-scaling-stroke"
        />
      )}
    </>
  );
}

const fadeUp = {
  hidden: { opacity: 1, y: 24 },
  visible: { opacity: 1, y: 0 }
};

function LandingPage() {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeUseCase, setActiveUseCase] = React.useState('interviews');
  const [isAutoPlayPaused, setIsAutoPlayPaused] = React.useState(false);
  const [fromLang, setFromLang] = React.useState('gujarati');
  const [toLang, setToLang] = React.useState('english');
  const [isTranslating, setIsTranslating] = React.useState(false);
  const [isNetAutoPaused, setIsNetAutoPaused] = React.useState(false);
  const appTarget = user ? getAuthenticatedHomePath(user) : '/register';

  const handleLangChange = (newFrom, newTo = toLang) => {
    setIsTranslating(true);
    setFromLang(newFrom);
    if (newFrom === newTo) {
      setToLang(newFrom === 'english' ? 'gujarati' : 'english');
    } else {
      setToLang(newTo);
    }
    setTimeout(() => setIsTranslating(false), 300);
  };

  // The right-hand column is the output side of the network, so picking one
  // of its nodes sets the target - picking it as the source was sending the
  // pair backwards (clicking English made English the language being spoken).
  const handleTargetChange = (newTo) => {
    setIsTranslating(true);
    setToLang(newTo);
    if (newTo === fromLang) {
      setFromLang(newTo === 'gujarati' ? 'hindi' : 'gujarati');
    }
    setTimeout(() => setIsTranslating(false), 300);
  };

  // A trace is lit when its language is one end of the current pair, so the
  // source trace, the engine and the target trace read as one path.
  const isTraceLive = (langKey) => langKey === fromLang || langKey === toLang;

  const scenario = USE_CASES.find((uc) => uc.id === activeUseCase) || USE_CASES[0];

  const sourceNodes = pickColumn(fromLang, SOURCE_NODES, [toLang]);
  const targetNodes = pickColumn(toLang, TARGET_NODES, sourceNodes);

  // A shared link such as /#pricing is opened before React has rendered the
  // sections, so the browser's own jump to the fragment finds nothing; and the
  // router (the Custom card, the /contact redirect) never scrolls at all. The
  // sections above the target (plans from the API, videos) also grow after
  // the first paint, so the target is re-aligned while the layout settles and
  // left alone as soon as the visitor scrolls themselves.
  React.useEffect(() => {
    const id = decodeURIComponent(location.hash.replace(/^#/, ''));
    if (!id) return undefined;

    const align = (behavior) => {
      document.getElementById(id)?.scrollIntoView?.({ behavior, block: 'start' });
    };
    align('smooth');

    if (typeof ResizeObserver === 'undefined') return undefined;
    let stopped = false;
    const stop = () => {
      if (stopped) return;
      stopped = true;
      observer.disconnect();
      clearTimeout(timer);
      USER_SCROLL_EVENTS.forEach((evt) => window.removeEventListener(evt, stop));
    };
    const observer = new ResizeObserver(() => {
      if (!stopped) align('auto');
    });
    observer.observe(document.body);
    const timer = setTimeout(stop, 3000);
    USER_SCROLL_EVENTS.forEach((evt) => window.addEventListener(evt, stop, { passive: true }));
    return stop;
  }, [location.hash, location.search, location.key]);

  // Mobile hamburger menu. Section links are plain anchors (as in the desktop
  // row) so the browser does the scrolling; the menu just closes behind them.
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const navRef = React.useRef(null);
  const menuToggleRef = React.useRef(null);
  const closeMenu = () => setIsMenuOpen(false);

  React.useEffect(() => {
    if (!isMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) setIsMenuOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      setIsMenuOpen(false);
      menuToggleRef.current?.focus();
    };
    // The menu is mobile-only; do not leave it open after widening the window.
    const onResize = () => {
      if (window.innerWidth > 900) setIsMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
    };
  }, [isMenuOpen]);

  React.useEffect(() => {
    if (isAutoPlayPaused) return;
    const interval = setInterval(() => {
      setActiveUseCase((prev) => {
        const nextIdx = (USE_CASES.findIndex((uc) => uc.id === prev) + 1) % USE_CASES.length;
        return USE_CASES[nextIdx].id;
      });
    }, 6000);
    return () => clearInterval(interval);
  }, [isAutoPlayPaused]);

  React.useEffect(() => {
    if (isNetAutoPaused) return;
    // Each column is one end of the pair, so the demo walks a source and a
    // target together and both halves of the network stay lit. The previous
    // list mixed the two columns into `from` alone, which left the output
    // side dark and showed an output language as the one being spoken.
    let step = 0;
    const interval = setInterval(() => {
      step += 1;
      setIsTranslating(true);
      setFromLang(SOURCE_NODES[step % SOURCE_NODES.length]);
      setToLang(TARGET_NODES[(step * 3) % TARGET_NODES.length]);
      setTimeout(() => setIsTranslating(false), 300);
    }, 3500);
    return () => clearInterval(interval);
  }, [isNetAutoPaused]);

  if (!isLoading && user) {
    return <Navigate to={getAuthenticatedHomePath(user)} replace />;
  }

  return (
    <div className="landing-page">
      <nav className="landing-nav" ref={navRef}>
        <div className="landing-nav-inner">
          <Link className="landing-brand" to="/">
            <span className="brand-icon-glow">
              <Icon name="waveform" size={20} />
            </span>
            <span className="brand-text">KrishvaQ</span>
          </Link>

          <div className="landing-links">
            <a href="#features">Features</a>
            <a href="#how-it-works">How It Works</a>
            <a href="#use-cases">Use Cases</a>
            <a href="#pricing">Pricing</a>
            <a href="#download">Download</a>
            <a href="#contact">Contact Us</a>
          </div>

          <div className="landing-actions">
            <Link className="nav-login-link" to="/login">Login</Link>
            <Link className="btn btn-get-started" to={appTarget}>
              Get Started
            </Link>
            <button
              type="button"
              ref={menuToggleRef}
              className="landing-menu-toggle"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              aria-controls="landing-mobile-menu"
              onClick={() => setIsMenuOpen((open) => !open)}
            >
              <Icon name={isMenuOpen ? 'x' : 'menu'} size={22} />
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className="landing-mobile-menu" id="landing-mobile-menu">
            <a href="#features" onClick={closeMenu}>Features</a>
            <a href="#how-it-works" onClick={closeMenu}>How It Works</a>
            <a href="#use-cases" onClick={closeMenu}>Use Cases</a>
            <a href="#pricing" onClick={closeMenu}>Pricing</a>
            <a href="#download" onClick={closeMenu}>Download</a>
            <a href="#contact" onClick={closeMenu}>Contact Us</a>
            <Link className="landing-mobile-menu-login" to="/login" onClick={closeMenu}>Login</Link>
          </div>
        )}
      </nav>

      <section className="landing-hero">
        {/* Subtle Grid Background */}
        <div className="hero-bg-grid" aria-hidden="true" />
        
        {/* Cyan Radial Ambient Glows */}
        <div className="hero-radial-glow glow-left" aria-hidden="true" />
        <div className="hero-radial-glow glow-right" aria-hidden="true" />
        <div className="hero-particles" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>

        {/* Full-width Horizontal Glowing Waveform Background SVG */}
        <div className="hero-waveform-bg" aria-hidden="true">
          <svg
            viewBox="0 0 1440 360"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="hero-cyan-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="var(--accent-ai)" stopOpacity="0.15" />
                <stop offset="25%" stopColor="var(--accent-ai)" stopOpacity="0.75" />
                <stop offset="50%" stopColor="var(--accent-ai)" stopOpacity="0.95" />
                <stop offset="75%" stopColor="var(--accent-ai)" stopOpacity="0.75" />
                <stop offset="100%" stopColor="var(--accent-ai)" stopOpacity="0.15" />
              </linearGradient>
              <filter id="hero-wave-glow" x="-20%" y="-30%" width="140%" height="160%">
                <feGaussianBlur stdDeviation="6" result="blur1" />
                <feGaussianBlur stdDeviation="16" result="blur2" />
                <feMerge>
                  <feMergeNode in="blur2" />
                  <feMergeNode in="blur1" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <path
              className="animated-hero-wave-1"
              d="M-50,180 Q120,80 300,180 T700,180 Q850,70 1000,180 Q1150,290 1300,180 T1550,180"
              stroke="var(--accent-ai)"
              strokeWidth="3"
            />
            <path
              className="animated-hero-wave-2"
              d="M-50,180 Q200,240 420,140 T880,200 T1320,150 T1550,180"
              stroke="var(--accent-ai)"
              strokeWidth="1.8"
              strokeOpacity="0.45"
              strokeDasharray="8 6"
            />
          </svg>
        </div>

        <div className="hero-container">
          {/* LEFT SIDE: COPY & CTAs & FEATURE CHIPS */}
          <motion.div
            className="landing-hero-copy"
            initial="hidden"
            animate="visible"
            transition={{ staggerChildren: 0.11, delayChildren: 0.1 }}
          >
            <motion.div className="landing-eyebrow" variants={fadeUp}>
              <span className="eyebrow-dot" />
              AI MEETING ASSISTANT
            </motion.div>
            
            <motion.h1 className="hero-title" variants={fadeUp}>
              Stay in the<br />
              conversation.<span className="hero-sparkle" aria-hidden="true">✦</span><br />
              <span className="hero-title-gradient">Let AI guide your</span><br />
              <span className="hero-title-gradient">next response.</span>
            </motion.h1>

            <motion.p className="landing-copy" variants={fadeUp}>
              KrishvaQ listens, understands, and translates in real time so you can respond with confidence.
            </motion.p>

            <motion.div className="landing-cta-row" variants={fadeUp}>
              <Link className="btn btn-hero-primary" to={appTarget}>
                Start Live Meeting <span className="btn-arrow" aria-hidden="true">→</span>
              </Link>
              <a className="btn btn-hero-secondary" href="#demo">
                Watch Demo <span className="btn-demo-icon">▷</span>
              </a>
            </motion.div>

            <motion.div className="hero-feature-chips" variants={fadeUp}>
              <div className="feature-chip">
                <Icon name="waveform" size={16} />
                <span>Real-time transcription</span>
              </div>
              <div className="feature-chip">
                <Icon name="language" size={16} />
                <span>AI translation</span>
              </div>
              <div className="feature-chip">
                <Icon name="zap" size={16} />
                <span>Smart suggestions</span>
              </div>
            </motion.div>
          </motion.div>

          {/* RIGHT SIDE: LOOPING PRODUCT PREVIEW */}
          <motion.div
            className="hero-video-wrap"
            initial={{ opacity: 0, scale: 0.96, x: 28 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <HeroVideoPreview />
          </motion.div>
        </div>
      </section>

      <DemoVideoSection />

      {/* HOW IT WORKS SECTION — REAL-TIME AI PROCESSING PIPELINE */}
      <section className="landing-section pipeline-section" id="how-it-works">
        <div className="pipeline-section-inner">
          {/* Top Centered Heading */}
          <div className="pipeline-header">
            <div className="landing-eyebrow">
              <span className="eyebrow-dot" />
              HOW IT WORKS
            </div>
            <h2 className="pipeline-title">From conversation to answer.</h2>
            <p className="pipeline-subtitle">
              KrishvaQ listens, understands, translates, and helps you respond in real-time.
            </p>
          </div>

          {/* Large Subtle Glass Container for 5-Stage Pipeline */}
          <div className="pipeline-container-panel" aria-label="Interactive AI Processing Pipeline">
            {/* Background Tech Grid Overlay & Ambient Radial Glow */}
            <div className="panel-bg-grid" aria-hidden="true" />
            <div className="panel-radial-glow" aria-hidden="true" />

            {/* Connecting Cyan Pipeline Line & Traveling Particle */}
            <div className="pipeline-track-container" aria-hidden="true">
              <div className="pipeline-track-line" />
              <div className="pipeline-particle-pulse" />
            </div>

            {/* 5 STAGES HORIZONTAL FLOW */}
            <div className="pipeline-stages-grid">
              {/* STAGE 01 — SPEAK */}
              <div className="pipeline-stage-card stage-01" tabIndex="0">
                <div className="stage-header-tag">01 SPEAK</div>
                <div className="stage-node-icon-wrap">
                  <span className="node-glow-ring" />
                  <Icon name="mic" size={22} className="stage-icon" />
                  <div className="soundwave-anim" aria-hidden="true">
                    <span className="sw-wave w1" />
                    <span className="sw-wave w2" />
                    <span className="sw-wave w3" />
                  </div>
                </div>
                <h3 className="stage-title">Speak</h3>
                <p className="stage-desc">Speak naturally in any language.</p>
                <div className="stage-visual-widget widget-speak">
                  <span className="widget-mic-badge">
                    <span className="mic-dot-cyan" /> Spoken Input Active
                  </span>
                </div>
              </div>

              {/* STAGE 02 — LISTEN */}
              <div className="pipeline-stage-card stage-02" tabIndex="0">
                <div className="stage-header-tag">02 LISTEN</div>
                <div className="stage-node-icon-wrap">
                  <span className="node-glow-ring" />
                  <Icon name="waveform" size={22} className="stage-icon" />
                </div>
                <h3 className="stage-title">Listen</h3>
                <p className="stage-desc">KrishvaQ captures your conversation in real-time.</p>
                <div className="stage-visual-widget widget-listen">
                  <div className="widget-waveform-bars" aria-hidden="true">
                    <span className="wb-bar" style={{ animationDelay: '0.1s', height: '40%' }} />
                    <span className="wb-bar" style={{ animationDelay: '0.3s', height: '80%' }} />
                    <span className="wb-bar" style={{ animationDelay: '0.2s', height: '100%' }} />
                    <span className="wb-bar" style={{ animationDelay: '0.4s', height: '60%' }} />
                    <span className="wb-bar" style={{ animationDelay: '0.15s', height: '90%' }} />
                    <span className="wb-bar" style={{ animationDelay: '0.35s', height: '50%' }} />
                  </div>
                  <span className="status-pill-listening">
                    <span className="live-dot-red" /> Listening
                  </span>
                </div>
              </div>

              {/* STAGE 03 — TRANSLATE */}
              <div className="pipeline-stage-card stage-03" tabIndex="0">
                <div className="stage-header-tag">03 TRANSLATE</div>
                <div className="stage-node-icon-wrap">
                  <span className="node-glow-ring" />
                  <Icon name="language" size={22} className="stage-icon" />
                </div>
                <h3 className="stage-title">Translate</h3>
                <p className="stage-desc">Convert conversations into your preferred language.</p>
                <div className="stage-visual-widget widget-translate">
                  <div className="lang-transition-pill">
                    <span className="lang-src">ગુજરાતી</span>
                    <span className="lang-flow-arrow">↓</span>
                    <span className="lang-tgt">English</span>
                  </div>
                </div>
              </div>

              {/* STAGE 04 — UNDERSTAND */}
              <div className="pipeline-stage-card stage-04" tabIndex="0">
                <div className="stage-header-tag">04 UNDERSTAND</div>
                <div className="stage-node-icon-wrap">
                  <span className="node-glow-ring" />
                  <Icon name="brain" size={22} className="stage-icon" />
                </div>
                <h3 className="stage-title">Understand</h3>
                <p className="stage-desc">AI understands the context and intent of the conversation.</p>
                <div className="stage-visual-widget widget-understand">
                  <div className="neural-node-vis" aria-hidden="true">
                    <span className="n-dot n1" />
                    <span className="n-dot n2" />
                    <span className="n-dot n3" />
                    <span className="n-line l1" />
                    <span className="n-line l2" />
                  </div>
                  <span className="status-pill-context">
                    <span className="badge-sparkle-dot">✦</span> Context detected
                  </span>
                </div>
              </div>

              {/* STAGE 05 — RESPOND */}
              <div className="pipeline-stage-card stage-05" tabIndex="0">
                <div className="stage-header-tag">05 RESPOND</div>
                <div className="stage-node-icon-wrap">
                  <span className="node-glow-ring" />
                  <Icon name="zap" size={22} className="stage-icon" />
                </div>
                <h3 className="stage-title">Respond</h3>
                <p className="stage-desc">Get a focused AI answer in real-time.</p>
                <div className="stage-visual-widget widget-respond">
                  <div className="respond-card-mini">
                    <span className="badge-answer-ready">
                      <span className="sparkle-icon">✦</span> Answer Ready
                    </span>
                    <div className="typing-text-preview">
                      <span className="typed-words">A mutex ensures single...</span>
                      <span className="typing-cursor" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* MINI LIVE EXAMPLE PANEL BELOW PIPELINE */}
          <div className="process-example-panel" aria-label="Real-time Process Transformation Example">
            <div className="example-header">
              <span className="example-eyebrow">
                <Icon name="sparkles" size={14} /> SEE THE PROCESS
              </span>
              <span className="example-caption">Real-time conversation transformation preview</span>
            </div>

            <div className="example-steps-flow">
              {/* STEP 01 */}
              <div className="example-step-card step-speaks">
                <div className="step-tag">STEP 01</div>
                <div className="step-label">
                  <span className="step-icon">🎙</span> USER SPEAKS
                </div>
                <div className="step-content-quote">
                  "આ પ્રોજેક્ટ ક્યારે પૂર્ણ થશે?"
                </div>
              </div>

              <div className="step-flow-arrow" aria-hidden="true">→</div>

              {/* STEP 02 */}
              <div className="example-step-card step-translation">
                <div className="step-tag">STEP 02</div>
                <div className="step-label">
                  <span className="step-icon">🌐</span> TRANSLATION
                </div>
                <div className="step-content-quote">
                  "When will this project be completed?"
                </div>
              </div>

              <div className="step-flow-arrow" aria-hidden="true">→</div>

              {/* STEP 03 */}
              <div className="example-step-card step-understands">
                <div className="step-tag">STEP 03</div>
                <div className="step-label">
                  <span className="step-icon">✦</span> AI UNDERSTANDS
                </div>
                <div className="step-intent-badge">
                  Intent detected: Project timeline
                </div>
              </div>

              <div className="step-flow-arrow" aria-hidden="true">→</div>

              {/* STEP 04 */}
              <div className="example-step-card step-response">
                <div className="step-tag">STEP 04</div>
                <div className="step-label">
                  <span className="step-icon">⚡</span> AI RESPONSE
                </div>
                <div className="step-content-quote text-cyan">
                  "The project is expected to be completed within two weeks."
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CORE FEATURES SECTION — ASYMMETRIC BENTO GRID */}
      <section className="landing-section bento-features-section" id="features">
        {/* Subtle background waveform layer */}
        <div className="bento-bg-waveform" aria-hidden="true" />

        <div className="bento-section-inner">
          {/* Centered Section Header */}
          <div className="bento-header">
            <div className="landing-eyebrow">
              <span className="eyebrow-dot" />
              POWERFUL BY DESIGN
            </div>
            <h2 className="bento-title">Everything happens in real-time.</h2>
            <p className="bento-subtitle">
              Transcribe, translate, understand, and respond without leaving the conversation.
            </p>
          </div>

          {/* Asymmetric Bento Grid */}
          <div className="bento-grid">
            {/* FEATURE 01 — REAL-TIME TRANSCRIPTION (Large Card: 2 cols x 2 rows) */}
            <div className="bento-card bento-card-large bento-card-transcription">
              <div className="bento-card-header">
                <div className="bento-card-icon-wrap">
                  <Icon name="waveform" size={22} />
                </div>
                <div className="bento-card-badge">REAL-TIME</div>
              </div>

              <div className="bento-card-info">
                <h3 className="bento-card-title">Real-Time Transcription</h3>
                <p className="bento-card-desc">Every word captured as the conversation happens.</p>
              </div>

              {/* Visual Demo Content */}
              <div className="bento-transcription-vis">
                {/* Large animated waveform with moving scanning cyan indicator */}
                <div className="large-waveform-box" aria-hidden="true">
                  <div className="waveform-scanner-line" />
                  <div className="wf-bar-set">
                    <span className="wf-b" style={{ height: '35%' }} />
                    <span className="wf-b" style={{ height: '65%' }} />
                    <span className="wf-b" style={{ height: '90%' }} />
                    <span className="wf-b" style={{ height: '40%' }} />
                    <span className="wf-b" style={{ height: '75%' }} />
                    <span className="wf-b" style={{ height: '100%' }} />
                    <span className="wf-b" style={{ height: '50%' }} />
                    <span className="wf-b" style={{ height: '80%' }} />
                    <span className="wf-b" style={{ height: '30%' }} />
                    <span className="wf-b" style={{ height: '95%' }} />
                    <span className="wf-b" style={{ height: '60%' }} />
                    <span className="wf-b" style={{ height: '85%' }} />
                    <span className="wf-b" style={{ height: '45%' }} />
                    <span className="wf-b" style={{ height: '70%' }} />
                    <span className="wf-b" style={{ height: '90%' }} />
                  </div>
                </div>

                {/* Small live transcript demo */}
                <div className="bento-live-transcript-snippet">
                  <div className="snippet-row">
                    <span className="snip-speaker">Interviewer</span>
                    <span className="snip-text">What is a Mutex?</span>
                  </div>
                  <div className="snippet-row">
                    <span className="snip-speaker candidate">Candidate</span>
                    <span className="snip-text hindi" lang="hi">म्यूटेक्स क्या है?</span>
                  </div>
                </div>
              </div>

              <div className="bento-card-footer">
                <span className="bento-status-listening">
                  <span className="listening-dot" /> Listening...
                </span>
                <span className="bento-lang-tag">Language detected: Hindi</span>
              </div>
            </div>

            {/* FEATURE 02 — LIVE TRANSLATION (Vertical Card: 1 col x 2 rows) */}
            <div className="bento-card bento-card-vertical bento-card-translation">
              <div className="bento-card-header">
                <div className="bento-card-icon-wrap">
                  <Icon name="language" size={22} />
                </div>
                <div className="bento-card-badge">LIVE PROCESSING</div>
              </div>

              <div className="bento-card-info">
                <h3 className="bento-card-title">Live Translation</h3>
                <p className="bento-card-desc">Follow conversations across languages.</p>
              </div>

              {/* Language flow visual */}
              <div className="bento-translation-vis">
                <div className="lang-box-src">
                  <span className="l-flag">🇮🇳 Hindi</span>
                  <p className="l-quote" lang="hi">"म्यूटेक्स क्या है?"</p>
                </div>

                <div className="lang-arrow-animated">↓</div>

                <div className="lang-box-tgt highlight-cyan">
                  <span className="l-flag cyan">🇬🇧 English</span>
                  <p className="l-quote">"What is a Mutex?"</p>
                </div>
              </div>

              <div className="bento-card-footer">
                <span className="bento-status-active">
                  <span className="active-dot" /> Translation active
                </span>
              </div>
            </div>

            {/* FEATURE 03 — AI-POWERED ANSWERS (Medium Card) */}
            <div className="bento-card bento-card-medium bento-card-ai">
              <div className="bento-card-header">
                <div className="bento-card-icon-wrap">
                  <Icon name="brain" size={22} />
                </div>
                <div className="bento-card-badge">AI ACTIVE</div>
              </div>

              <div className="bento-card-info">
                <h3 className="bento-card-title">AI-Powered Answers</h3>
                <p className="bento-card-desc">Understand intent and generate focused responses.</p>
              </div>

              <div className="bento-ai-response-box">
                <div className="ai-box-top">
                  <span className="ai-sparkle-tag">✦ AI KRISHVAQ</span>
                  <span className="ai-ready-badge">Answer Ready</span>
                </div>
                <p className="ai-snippet-text">
                  A mutex is a synchronization mechanism that controls access to a shared resource...
                </p>
                <div className="ai-confidence-meter">
                  <div className="meter-label">
                    <span>Confidence</span>
                    <span className="meter-val">96%</span>
                  </div>
                  <div className="meter-track">
                    <div className="meter-fill" style={{ width: '96%' }} />
                  </div>
                </div>
              </div>
            </div>

            {/* FEATURE 04 — MULTILINGUAL MEETINGS (Medium Card) */}
            <div className="bento-card bento-card-medium bento-card-multilingual">
              <div className="bento-card-header">
                <div className="bento-card-icon-wrap">
                  <Icon name="globe" size={22} />
                </div>
                <div className="bento-card-badge">MULTILINGUAL</div>
              </div>

              <div className="bento-card-info">
                <h3 className="bento-card-title">Multilingual Meetings</h3>
                <p className="bento-card-desc">Communicate naturally across languages.</p>
              </div>

              <BouncingLanguages />
            </div>

            {/* FEATURE 05 — MEETING HISTORY (Wide Card: 2 cols x 1 row) */}
            <div className="bento-card bento-card-wide bento-card-history">
              <div className="bento-card-header">
                <div className="bento-card-icon-wrap">
                  <Icon name="clock" size={22} />
                </div>
                <div className="bento-card-badge">HISTORY</div>
                <span className="history-view-link">View all →</span>
              </div>

              <div className="bento-card-info">
                <h3 className="bento-card-title">Meeting History</h3>
                <p className="bento-card-desc">Your conversations stay organized after every meeting.</p>
              </div>

              <div className="bento-timeline-vis">
                <div className="timeline-group">
                  <div className="group-label">TODAY</div>
                  <div className="timeline-item active-latest">
                    <span className="tl-dot cyan-glow" />
                    <span className="tl-time">10:31 AM</span>
                    <span className="tl-title">Technical Interview</span>
                    <span className="tl-langs">Hindi → English</span>
                  </div>
                  <div className="timeline-item">
                    <span className="tl-dot" />
                    <span className="tl-time">09:15 AM</span>
                    <span className="tl-title">Client Discussion</span>
                    <span className="tl-langs">English</span>
                  </div>
                </div>

                <div className="timeline-group">
                  <div className="group-label">YESTERDAY</div>
                  <div className="timeline-item">
                    <span className="tl-dot" />
                    <span className="tl-time">04:20 PM</span>
                    <span className="tl-title">Product Review</span>
                    <span className="tl-langs">Hindi → English</span>
                  </div>
                </div>
              </div>
            </div>

            {/* FEATURE 06 — MEETING SUMMARIES (Compact Card: 1 col x 1 row) */}
            <div className="bento-card bento-card-compact bento-card-summary">
              <div className="bento-card-header">
                <div className="bento-card-icon-wrap">
                  <Icon name="file-text" size={22} />
                </div>
                <div className="bento-card-badge">AI RECAP</div>
              </div>

              <div className="bento-card-info">
                <h3 className="bento-card-title">Meeting Summaries</h3>
                <p className="bento-card-desc">Turn conversations into clear takeaways and next steps.</p>
              </div>

              <div className="bento-summary-vis">
                <div className="bento-summary-item">
                  <span className="bento-summary-label">KEY TAKEAWAY</span>
                  <p>A mutex keeps shared resources safe by allowing one thread at a time.</p>
                </div>
                <div className="bento-summary-item">
                  <span className="bento-summary-label">NEXT STEP</span>
                  <p>Review a thread-safe implementation before the next interview.</p>
                </div>
              </div>

              <div className="bento-card-footer">
                <span className="bento-status-tag">Key points. Clear next steps.</span>
              </div>
            </div>
          </div>

          {/* SECTION BOTTOM STATEMENT */}
          <div className="bento-bottom-statement">
            <div className="bento-statement-main">
              One conversation. <span className="bento-text-cyan">Multiple layers of intelligence.</span>
            </div>
            <div className="bento-statement-sub">
              Listen → Translate → Understand → Respond
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
         PRICING SECTION — FUTURISTIC AI PLAN SELECTOR
         ========================================================================== */}
      <section className="landing-section pricing-section" id="pricing">
        {/* Background Grid & Subtle Ambient Glow */}
        <div className="pricing-bg-grid" aria-hidden="true" />
        <div className="pricing-radial-glow" aria-hidden="true" />

        <div className="pricing-section-inner">
          {/* Heading, the five cards from GET /api/billing/plans and what
              every plan includes. The FAQ and the "Need help choosing?" line
              live on /pricing. Custom / Team points at the form further down
              this page. */}
          <PublicPricing
            showFaq={false}
            showFooterCta={false}
            customPlanTo="/?reason=custom-plan#contact"
          />
        </div>
      </section>

      {/* USE CASES SECTION — CONVERSATION STORIES TIMELINE */}
      <section className="landing-section conversation-stories-section" id="use-cases">
        {/* Subtle Background Grid & Glow */}
        <div className="cs-bg-grid" aria-hidden="true" />
        <div className="cs-bg-radial" aria-hidden="true" />

        <div className="cs-section-inner">
          {/* Compact Centered Header */}
          <div className="cs-header">
            <div className="landing-eyebrow">
              <span className="eyebrow-dot" />
              BUILT FOR REAL CONVERSATIONS
            </div>
            <h2 className="cs-title">One AI. Different moments.</h2>
            <p className="cs-subtitle">
              From interviews to meetings, KrishvaQ understands what's happening and helps you respond in real-time.
            </p>
          </div>

          {/* Scenario selector */}
          <div
            className="cs-scenario-tabs-wrapper"
            onMouseEnter={() => setIsAutoPlayPaused(true)}
            onMouseLeave={() => setIsAutoPlayPaused(false)}
            role="tablist"
            aria-label="Conversation Scenarios"
          >
            {USE_CASES.map((uc) => (
              <button
                key={uc.id}
                type="button"
                className={`cs-tab-btn ${activeUseCase === uc.id ? 'active' : ''}`}
                onClick={() => setActiveUseCase(uc.id)}
                role="tab"
                aria-selected={activeUseCase === uc.id}
              >
                <Icon name={uc.icon} size={15} />
                <span>{uc.title}</span>
              </button>
            ))}
          </div>

          {/* THE LIVE PANEL — what is heard on the left, what KrishvaQ makes
              of it on the right: the same split as the product's own overlay,
              and it fills a wide panel that an alternating timeline left half
              empty. `key` remounts on every scenario change so the entrance
              sequence replays; `--steps` lets the right-hand column wait for
              the transcript to finish arriving. */}
          <div
            key={scenario.id}
            className="cs-live-panel"
            style={{ '--steps': scenario.transcript.length }}
            onMouseEnter={() => setIsAutoPlayPaused(true)}
            onMouseLeave={() => setIsAutoPlayPaused(false)}
          >
            <div className="cs-panel-col">
              <div className="cs-col-head">
                <span className="cs-col-title">LIVE TRANSCRIPT</span>
                <span className="cs-live-indicator">
                  <span className="live-dot-pulse" /> LIVE
                </span>
              </div>

              <ol className="cs-feed">
                {scenario.transcript.map((ev, i) => (
                  <li key={ev.label + ev.time} className="cs-feed-item" style={{ '--i': i }}>
                    <span className="cs-feed-rail" aria-hidden="true">
                      <span className={`cs-feed-dot ${ev.kind === 'speech' ? 'is-speech' : ''}`} />
                    </span>

                    <div className="cs-feed-body">
                      <time className="cs-feed-time">{ev.time}</time>

                      {ev.kind === 'speech' ? (
                        <div className="cs-speech-bubble">
                          <div className="cs-speaker-label">{ev.label}</div>
                          <p className="cs-bubble-text">{ev.text}</p>
                        </div>
                      ) : (
                        <div className={`cs-system-block ${ev.translation ? 'cs-translation-event' : ''}`}>
                          <div className="cs-sys-header">
                            <span className="cs-sys-icon">{ev.icon || '✦'}</span>
                            <span className="cs-sys-title">{ev.label}</span>
                          </div>
                          <div className="cs-sys-detail">
                            {ev.detail}
                            {ev.value && (
                              <span className={CS_VALUE_CLASS[ev.valueTone] || CS_VALUE_CLASS.plain}>
                                {ev.value}
                              </span>
                            )}
                            {ev.wave && (
                              <div className="cs-mini-wave">
                                <span className="mw-b" style={{ height: '50%' }} />
                                <span className="mw-b" style={{ height: '90%' }} />
                                <span className="mw-b" style={{ height: '60%' }} />
                              </div>
                            )}
                            {ev.tags && (
                              <div className="cs-tag-group">
                                {ev.tags.map((tag) => (
                                  <span key={tag.text} className={`cs-tag-badge ${tag.tone}`}>{tag.text}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <aside className="cs-panel-col cs-ai-col">
              <div className="cs-col-head">
                <span className="cs-col-title">KRISHVAQ AI</span>
                <span className="cs-scenario-badge">{scenario.badge}</span>
              </div>

              <div className="cs-ai-insight">
                <span className="cs-insight-lbl">Topic detected</span>
                <span className="cs-insight-val">{scenario.topic}</span>
                <div className="cs-conf" style={{ '--conf': `${scenario.confidence}%` }}>
                  <span className="cs-conf-track"><span className="cs-conf-fill" /></span>
                  <span className="cs-conf-num">{scenario.confidence}% confident</span>
                </div>
              </div>

              <div className="cs-ai-response-card">
                <div className="cs-ai-header">
                  <span className="cs-ai-brand">✦ {scenario.ai.brand}</span>
                  <time className="cs-feed-time">{scenario.ai.time}</time>
                </div>
                <p className="cs-ai-text">{scenario.ai.text}</p>
              </div>

              {scenario.ai.ready && (
                <div className="cs-status-pill-ready">
                  <span className="ready-dot" /> {scenario.ai.ready.label}
                  {scenario.ai.ready.readAloud && (
                    <button type="button" className="btn-read-action">
                      <Icon name="volume" size={13} /> Read Aloud
                    </button>
                  )}
                </div>
              )}
            </aside>
          </div>

          {/* BOTTOM STATEMENT */}
          <div className="cs-bottom-statement">
            <div className="cs-statement-main">
              The conversation keeps moving. <span className="text-cyan-glow">KrishvaQ keeps up.</span>
            </div>
            <div className="cs-statement-sub">
              Listen. Understand. Respond.
            </div>
          </div>
        </div>
      </section>

      {/* MULTILINGUAL EXPERIENCE SECTION — LANGUAGE NETWORK */}
      <section className="landing-section language-network-section" id="languages">
        <div className="network-bg-grid" aria-hidden="true" />
        <div className="network-radial-glow" aria-hidden="true" />

        <div className="network-section-inner">
          {/* Top Centered Section Heading */}
          <div className="network-header">
            <div className="landing-eyebrow">
              <span className="eyebrow-dot" />
              MULTILINGUAL INTELLIGENCE
            </div>
            <h2 className="network-title">
              One conversation. <span className="text-cyan">Any language.</span>
            </h2>
            <p className="network-subtitle">
              KrishvaQ translates conversations in real-time so language never gets in the way.
            </p>
          </div>

          {/* MAIN LANGUAGE NETWORK VISUALIZATION */}
          <div 
            className="language-network-container"
            onMouseEnter={() => setIsNetAutoPaused(true)}
            onMouseLeave={() => setIsNetAutoPaused(false)}
          >
            {/* Column labels. These sit over the two node columns and say
                which side of the translation each one is - unlabelled, the
                diagram was eight cards in two arbitrary groups. "Language
                detected" was over the output column, where nothing is
                detected; the engine's own label sits under the engine. */}
            <div className="network-hud-tag tag-top-left">
              <span className="hud-dot-cyan" /> SPOKEN — LANGUAGE DETECTED
            </div>
            <div className="network-hud-tag tag-top-right">
              <span className="hud-dot-cyan" /> TRANSLATED TO
            </div>
            <div className="network-hud-tag tag-bottom-center">
              <span className="hud-dot-cyan" /> CONTEXT PRESERVED
            </div>

            {/* SVG Connecting Traces Layer. The viewBox is stretched over
                the container, so its x units are percentages of the width and
                the endpoints hold at any size above 1024px, below which this
                layer is hidden. Each trace is drawn from the same list as the
                cards, so a trace always belongs to the card it reaches. */}
            <svg className="network-svg-traces" viewBox="0 0 1000 440" preserveAspectRatio="none" aria-hidden="true">
              {sourceNodes.map((langKey, i) => (
                <NetworkTrace key={langKey} d={TRACE_PATHS_IN[i]} live={isTraceLive(langKey)} direction="in" />
              ))}
              {targetNodes.map((langKey, i) => (
                <NetworkTrace key={langKey} d={TRACE_PATHS_OUT[i]} live={isTraceLive(langKey)} direction="out" />
              ))}
            </svg>

            {/* LEFT COLUMN: Input Language Nodes */}
            <div className="network-lang-col left-col">
              {sourceNodes.map((langKey) => {
                const lang = SUPPORTED_LANGUAGES[langKey];
                const isActive = fromLang === langKey;
                return (
                  <button
                    key={langKey}
                    type="button"
                    className={`lang-node-card ${isActive ? 'active-node' : ''}`}
                    onClick={() => handleLangChange(langKey)}
                    title={`Translate from ${lang.name}`}
                    aria-pressed={isActive}
                    aria-label={`Select ${lang.name} as source language`}
                  >
                    <div className="ln-header">
                      <span className="ln-flag">{lang.flag}</span>
                      <span className="ln-name">{lang.name}</span>
                    </div>
                    <div className="ln-script">{lang.native}</div>
                    {isActive && (
                      <div className="ln-mini-wave">
                        <span className="mw-b" style={{ height: '80%' }} />
                        <span className="mw-b" style={{ height: '100%' }} />
                        <span className="mw-b" style={{ height: '60%' }} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* CENTER: Glowing AI Engine Node */}
            <div className="network-center-engine">
              <div className="engine-outer-ring pulse-ring-anim" />
              <div className="engine-core-node">
                <div className="engine-icon-wrap">
                  <Icon name="waveform" size={32} />
                </div>
                <div className="engine-brand">✦ KRISHVAQ</div>
                <div className="engine-subtag">AI LANGUAGE ENGINE</div>
              </div>
            </div>

            {/* RIGHT COLUMN: Output Language Nodes */}
            <div className="network-lang-col right-col">
              {targetNodes.map((langKey) => {
                const lang = SUPPORTED_LANGUAGES[langKey];
                const isActive = toLang === langKey;
                return (
                  <button
                    key={langKey}
                    type="button"
                    className={`lang-node-card ${isActive ? 'active-node' : ''}`}
                    onClick={() => handleTargetChange(langKey)}
                    title={`Translate to ${lang.name}`}
                    aria-pressed={isActive}
                    aria-label={`Select ${lang.name} as target language`}
                  >
                    <div className="ln-header">
                      <span className="ln-flag">{lang.flag}</span>
                      <span className="ln-name">{lang.name}</span>
                    </div>
                    <div className="ln-script">{lang.native}</div>
                    {/* The waveform belongs to the side being spoken; on the
                        output node it read as the translation talking back. */}
                    {isActive && <span className="ln-out-mark" aria-hidden="true">✓</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* FLOATING LIVE TRANSLATION PANEL */}
          <div className="live-translation-panel-wrap">
            <div className="live-translation-card">
              <div className="lt-card-header">
                <div className="lt-header-left">
                  <span className="lt-title">LIVE TRANSLATION</span>
                  <span className="lt-live-badge"><span className="live-dot" /> LIVE</span>
                </div>
                {/* Language Selectors */}
                <div className="lt-selector-row">
                  <div className="select-group">
                    <span className="select-label">FROM</span>
                    <select
                      className="lt-select"
                      value={fromLang}
                      onChange={(e) => handleLangChange(e.target.value)}
                      aria-label="Select source language"
                    >
                      {Object.keys(SUPPORTED_LANGUAGES).map((key) => (
                        <option key={key} value={key}>
                          {SUPPORTED_LANGUAGES[key].flag} {SUPPORTED_LANGUAGES[key].name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <span className="select-arrow-between">→</span>

                  <div className="select-group">
                    <span className="select-label">TO</span>
                    <select
                      className="lt-select"
                      value={toLang}
                      onChange={(e) => setToLang(e.target.value)}
                      aria-label="Select target language"
                    >
                      {Object.keys(SUPPORTED_LANGUAGES).map((key) => (
                        <option key={key} value={key}>
                          {SUPPORTED_LANGUAGES[key].flag} {SUPPORTED_LANGUAGES[key].name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Translation Content Area */}
              <div className="lt-card-body">
                {/* Original Source Language */}
                <div className="lt-source-block">
                  <span className="lt-lang-tag muted">
                    {SUPPORTED_LANGUAGES[fromLang]?.flag} {SUPPORTED_LANGUAGES[fromLang]?.name.toUpperCase()}
                  </span>
                  <p className="lt-text-source">
                    "{SUPPORTED_LANGUAGES[fromLang]?.text}"
                  </p>
                </div>

                {/* Real-Time Processing Transition */}
                <div className="lt-transition-bar">
                  <span className="lt-arrow-down">↓</span>
                  <span className="lt-status-pulse">
                    {isTranslating ? '✦ TRANSLATING...' : '✦ LIVE STREAMING'}
                  </span>
                  <span className="lt-arrow-down">↓</span>
                </div>

                {/* Translated Target Language Result */}
                <div className="lt-target-block">
                  <span className="lt-lang-tag cyan">
                    {SUPPORTED_LANGUAGES[toLang]?.flag} {SUPPORTED_LANGUAGES[toLang]?.name.toUpperCase()}
                  </span>
                  <p className={`lt-text-result ${isTranslating ? 'text-fade-out' : 'text-fade-in'}`}>
                    "{SUPPORTED_LANGUAGES[toLang]?.text}"
                  </p>
                </div>
              </div>

              {/* Card Footer with Context Aware Badge & Latency */}
              <div className="lt-card-footer">
                <div className="context-aware-badge">
                  <span className="ca-sparkle">✦ CONTEXT AWARE</span>
                  <span className="ca-label">Meeting schedule</span>
                </div>
                <div className="lt-latency-info">
                  Translation completed in <span className="text-cyan font-mono">0.4s</span>
                </div>
              </div>
            </div>
          </div>

          {/* MINIMAL CAPABILITY STRIP */}
          <div className="network-capability-strip">
            <span className="net-cap-item">REAL-TIME TRANSLATION</span>
            <span className="net-cap-sep">|</span>
            <span className="net-cap-item">MULTILINGUAL CONVERSATIONS</span>
            <span className="net-cap-sep">|</span>
            <span className="net-cap-item">CONTEXT-AWARE UNDERSTANDING</span>
            <span className="net-cap-sep">|</span>
            <span className="net-cap-item">NATURAL AI RESPONSES</span>
          </div>

          {/* SECTION CTA */}
          <div className="network-cta-wrap">
            <Link to={appTarget} className="btn btn-primary btn-network-cta">
              Start a multilingual conversation →
            </Link>
          </div>
        </div>
      </section>

      {/* ==========================================================================
         DESKTOP DOWNLOAD
         ========================================================================== */}
      {/* A section rather than a link out to /download: every other item
         in the nav scrolls this page, and sending someone to a separate
         page mid-scroll loses the thread they were following. The page
         still exists at /download for direct and marketing links, and
         renders this same DesktopDownload block. */}
      <section className="landing-section landing-download-section" id="download">
        <div className="landing-download-inner">
          <div className="landing-download-panel">
            <DesktopDownload presentation="landing" />
          </div>
        </div>
      </section>

      {/* CONTACT US - a section of this page rather than a page of its
          own. ?reason=custom-plan (the pricing Custom card) preselects
          "Custom Plan". */}
      <section className="landing-section pricing-section contact-section" id="contact">
        {/* Same grid and glow as the pricing section. */}
        <div className="pricing-bg-grid" aria-hidden="true" />
        <div className="pricing-radial-glow" aria-hidden="true" />

        <div className="pricing-section-inner">
          <div className="pricing-header">
            <div className="landing-eyebrow">
              <span className="eyebrow-dot" />
              GET IN TOUCH
            </div>
            <h2 className="pricing-title">Contact Us</h2>
            <p className="pricing-subtitle">
              Have a question or need a plan that fits your needs? Send us
              a message and we&apos;ll get back to you.
            </p>
          </div>

          <div className="contact-panel">
            <div className="contact-panel-intro">
              <h3 className="contact-panel-title">
                Tell us what you need.
                <span className="contact-panel-accent"> We&apos;ll take it from there.</span>
              </h3>
              <ul className="pc-features-list contact-panel-points">
                <li>
                  <span className="check-icon cyan">{'✓'}</span>
                  <span>Questions about KrishvaQ, your account or your plan</span>
                </li>
                <li>
                  <span className="check-icon cyan">{'✓'}</span>
                  <span>A Custom plan when you need more meetings each month</span>
                </li>
                <li>
                  <span className="check-icon cyan">{'✓'}</span>
                  <span>Every KrishvaQ feature included, whatever the plan</span>
                </li>
                <li>
                  <span className="check-icon cyan">{'✓'}</span>
                  <span>We reply to the email address you give us</span>
                </li>
              </ul>
            </div>

            <div className="contact-panel-form contact-card">
              <ContactForm reason={searchParams.get('reason')} />
            </div>
          </div>
        </div>
      </section>

      {/* ==========================================================================
         PREMIUM AI COMMAND CENTER FOOTER & CLOSING CTA
         ========================================================================== */}
      <footer className="landing-footer-container">
        {/* Subtle Radial Glow & Technical Background Grid */}
        <div className="footer-bg-grid" aria-hidden="true" />
        <div className="footer-radial-glow" aria-hidden="true" />

        {/* PART 1: LARGE CLOSING CTA */}
        <div className="footer-closing-cta">
          <div className="landing-eyebrow eyebrow-cyan">
            <span className="eyebrow-dot" />
            READY WHEN YOU ARE
          </div>
          <h2 className="footer-cta-heading">
            Your next conversation <span className="text-cyan-glow">starts here.</span>
          </h2>
          <p className="footer-cta-subtitle">
            Listen, translate, understand, and respond in real-time with KrishvaQ.
          </p>

          {/* Animated Waveform Accent */}
          <div className="footer-cta-waveform" aria-hidden="true">
            <svg viewBox="0 0 400 24" className="cta-wave-svg" fill="none">
              <path
                d="M0 12 Q20 4, 40 12 T80 12 T120 12 T160 2 T200 22 T240 12 T280 12 T320 12 T360 20 T400 12"
                stroke="var(--accent-ai)"
                strokeWidth="1.5"
                strokeOpacity="0.4"
              />
            </svg>
          </div>

          {/* CTA Action Buttons */}
          <div className="footer-cta-actions">
            <Link to={appTarget} className="btn btn-primary btn-footer-primary">
              Start a Meeting →
            </Link>
            <a href="#how-it-works" className="footer-link-secondary">
              See How It Works →
            </a>
          </div>
        </div>

        <div className="footer-divider-line" aria-hidden="true" />

        {/* PART 2: SOPHISTICATED MULTI-COLUMN FOOTER */}
        <div className="footer-main-content">
          <div className="footer-columns-grid">
            {/* BRAND COLUMN (LARGER) */}
            <div className="footer-col brand-col">
              <div className="footer-brand-logo">
                <span className="brand-sparkle">✦</span> KrishvaQ
              </div>
              <p className="footer-brand-desc">
                Real-time AI assistance for conversations.
              </p>
              <div className="footer-status-pill">
                <span className="status-dot-pulse" /> READY TO ASSIST
              </div>
            </div>

            {/* PRODUCT COLUMN */}
            <div className="footer-col">
              <div className="footer-col-heading">Product</div>
              <ul className="footer-nav-list">
                <li><a href="#features">Features</a></li>
                <li><a href="#languages">Languages</a></li>
                <li><Link to="/register">Meeting History</Link></li>
                <li><a href="#features">AI Answers</a></li>
                <li><a href="#features">Meeting Summaries</a></li>
              </ul>
            </div>

            {/* EXPERIENCE COLUMN */}
            <div className="footer-col">
              <div className="footer-col-heading">Experience</div>
              <ul className="footer-nav-list">
                <li><a href="#how-it-works">How It Works</a></li>
                <li><a href="#use-cases">Use Cases</a></li>
                <li><a href="#languages">Live Translation</a></li>
                <li><a href="#languages">Multilingual Meetings</a></li>
              </ul>
            </div>

            {/* RESOURCES COLUMN */}
            <div className="footer-col">
              <div className="footer-col-heading">Resources</div>
              <ul className="footer-nav-list">
                <li><a href="#how-it-works">Getting Started</a></li>
                <li><a href="#features">Product Guide</a></li>
                <li><a href="#features">Documentation</a></li>
                <li><a href="#contact">Contact Us</a></li>
              </ul>
            </div>

            {/* ACCOUNT COLUMN */}
            <div className="footer-col">
              <div className="footer-col-heading">Account</div>
              <ul className="footer-nav-list">
                {user ? (
                  <>
                    <li><Link to="/app/dashboard">Dashboard</Link></li>
                    <li><Link to="/app/meeting/new">Start Meeting</Link></li>
                  </>
                ) : (
                  <>
                    <li><Link to="/register">Register</Link></li>
                    <li><Link to="/login">Login</Link></li>
                  </>
                )}
              </ul>
            </div>
          </div>

          {/* BOTTOM BAR */}
          <div className="footer-bottom-bar">
            <div className="fbb-left">© 2026 KrishvaQ</div>
            <div className="fbb-center">
              {/* Real links now - these were <span>s styled to look
                  like links, so the footer appeared to offer a privacy
                  policy that could not be reached. */}
              <Link className="fbb-legal-link" to="/privacy-policy">Privacy Policy</Link>
              <Link className="fbb-legal-link" to="/terms-of-service">Terms of Service</Link>
            </div>
            <div className="fbb-right">Built for conversations that matter.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
